import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const KVThumbnails = ({ installation, size = 'sm' }) => {
  const { oldKVId, newKVId, kvThumbnails, status } = installation || {};
  
  const thumbnailSize = size === 'lg' ? 'w-16 h-16' : size === 'md' ? 'w-12 h-12' : 'w-8 h-8';
  const iconSize = size === 'lg' ? 16 : size === 'md' ? 12 : 8;

  // NEW: When Status = Verified → show ONLY the current (new) KV
  if (status?.toLowerCase() === 'verified' && newKVId) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {/* Show only New/Current KV Thumbnail */}
          <div className={`${thumbnailSize} rounded border border-border overflow-hidden bg-muted/30 flex-shrink-0`}>
            <Image
              src={kvThumbnails?.new?.thumbnail || kvThumbnails?.new?.url}
              alt={`KV ${newKVId}`}
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full flex items-center justify-center bg-blue-50">
                  <Icon name="Image" size={iconSize} className="text-blue-400" />
                </div>
              }
            />
          </div>
          <span className="font-mono text-xs text-foreground">{newKVId}</span>
        </div>
        
        {/* Video duration if available */}
        {kvThumbnails?.new?.duration && (
          <div className="text-xs text-muted-foreground">
            Duration: {kvThumbnails?.new?.duration}
          </div>
        )}
      </div>
    );
  }

  // Case 1: KV Archive Replacement (Old_KV_ID not null) - For Planned or Installed status
  if (oldKVId && newKVId && status?.toLowerCase() !== 'verified') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {/* Old KV Thumbnail */}
          <div className="flex items-center gap-1">
            <div className={`${thumbnailSize} rounded border border-border overflow-hidden bg-muted/30 flex-shrink-0`}>
              <Image
                src={kvThumbnails?.old?.thumbnail || kvThumbnails?.old?.url}
                alt={`Old KV ${oldKVId}`}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full flex items-center justify-center bg-red-50">
                    <Icon name="Image" size={iconSize} className="text-red-400" />
                  </div>
                }
              />
            </div>
            <span className="font-mono text-xs text-red-600">{oldKVId}</span>
          </div>

          {/* Arrow */}
          <Icon name="ArrowRight" size={10} className="text-muted-foreground flex-shrink-0" />

          {/* New KV Thumbnail */}
          <div className="flex items-center gap-1">
            <div className={`${thumbnailSize} rounded border border-border overflow-hidden bg-muted/30 flex-shrink-0`}>
              <Image
                src={kvThumbnails?.new?.thumbnail || kvThumbnails?.new?.url}
                alt={`New KV ${newKVId}`}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full flex items-center justify-center bg-green-50">
                    <Icon name="Image" size={iconSize} className="text-green-400" />
                  </div>
                }
              />
            </div>
            <span className="font-mono text-xs text-green-600">{newKVId}</span>
          </div>
        </div>
        
        {/* Video duration indicators if available */}
        {(kvThumbnails?.old?.duration || kvThumbnails?.new?.duration) && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {kvThumbnails?.old?.duration && (
              <span>Old: {kvThumbnails?.old?.duration}</span>
            )}
            {kvThumbnails?.old?.duration && kvThumbnails?.new?.duration && (
              <span>•</span>
            )}
            {kvThumbnails?.new?.duration && (
              <span>New: {kvThumbnails?.new?.duration}</span>
            )}
          </div>
        )}
        
        {/* Replacement type info - No need for "KV Archive Replacement" note as per requirement */}
      </div>
    );
  }

  // Case 2: New Installation (Old_KV_ID is null) - Show ONLY New KV with "New" label
  if (!oldKVId && newKVId && status?.toLowerCase() !== 'verified') {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {/* New KV Thumbnail */}
          <div className={`${thumbnailSize} rounded border border-border overflow-hidden bg-muted/30 flex-shrink-0`}>
            <Image
              src={kvThumbnails?.new?.thumbnail || kvThumbnails?.new?.url}
              alt={`KV ${newKVId}`}
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full flex items-center justify-center bg-blue-50">
                  <Icon name="Image" size={iconSize} className="text-blue-400" />
                </div>
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-foreground">{newKVId}</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
              New
            </span>
          </div>
        </div>
        
        {/* Video duration if available */}
        {kvThumbnails?.new?.duration && (
          <div className="text-xs text-muted-foreground">
            Duration: {kvThumbnails?.new?.duration}
          </div>
        )}
      </div>
    );
  }

  // Case 3: No KV information available - Return null to prevent any display
  return null;
};

export default KVThumbnails;